import React, { useLayoutEffect } from 'react';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { PERFORMANCE_MARK } from '@atlassian/jira-onboarding-checklist-next/src/common/constants.tsx';
import {
	openMenuItemLoadTime,
	qsButtonAutoLoadTime,
	qsChecklistAutoLoadTime,
} from '@atlassian/jira-onboarding-checklist-next/src/common/utils/performance-metrics.tsx';
import { addChunkLoadErrorAnalyticsAttribute } from '@atlassian/jira-onboarding-core/src/utils/chunk-error-logging/index.tsx';
import { QuickstartPillVisibilityContainer } from '@atlassian/jira-quickstart-pill-visibility/src/controllers/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import { JswOnboardingChecklistNext } from '@atlassian/jira-onboarding-checklist-next/src/ui/onboarding-checklist/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Props, OpenMenuItem as OpenMenuItemType } from './open-menu-item/index.tsx';
import { JswOnboardingButtonNext as JswOnboardingButtonNextSync } from './onboarding-button/index.tsx';

const FallbackChecklist = () => {
	useLayoutEffect(() => {
		qsChecklistAutoLoadTime.start();
	}, []);
	return <></>;
};

export const JswOnboardingChecklist = (): React.JSX.Element => (
	<UFOSegment name="quickstart">
		<JSErrorBoundary
			id="asyncChecklistComponent"
			packageName="jiraSoftwareOnboardingQuickstart"
			teamName="jlove-makkuro"
			fallback="unmount"
			attributes={addChunkLoadErrorAnalyticsAttribute}
		>
			<Placeholder name="software-onboarding-checklist-new" fallback={<FallbackChecklist />}>
				<JswOnboardingChecklistNext />
			</Placeholder>
		</JSErrorBoundary>
	</UFOSegment>
);

const FallbackButton = () => {
	useLayoutEffect(() => {
		qsButtonAutoLoadTime.start();
	}, []);
	return <></>;
};

export const JswOnboardingButton = (): React.JSX.Element => (
	<JSErrorBoundary
		id="asyncChecklistButtonComponent"
		packageName="jiraSoftwareOnboardingQuickstart"
		teamName="jlove-makkuro"
		fallback="unmount"
		attributes={addChunkLoadErrorAnalyticsAttribute}
	>
		<Placeholder name="software-onboarding-checklist-button-new" fallback={<FallbackButton />}>
			<QuickstartPillVisibilityContainer scope="ListPageQuickstartPill">
				<JswOnboardingButtonNextSync />
			</QuickstartPillVisibilityContainer>
		</Placeholder>
	</JSErrorBoundary>
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LazyOpenMenuItem = lazy<typeof OpenMenuItemType>(() =>
	import(
		/* webpackChunkName: "async-software-onboarding-checklist-open-menu-item" */ './open-menu-item'
	).then(({ OpenMenuItem }) => {
		if (!fg('jira-onboarding-disable-bm3-ufo-instrument')) {
			openMenuItemLoadTime.mark(PERFORMANCE_MARK.BUNDLE_LOAD_END);
		}
		return OpenMenuItem;
	}),
);

const SubmitOpenMenuItemLoadStart = () => {
	useLayoutEffect(() => {
		if (!fg('jira-onboarding-disable-bm3-ufo-instrument')) {
			openMenuItemLoadTime.start();
			openMenuItemLoadTime.mark(PERFORMANCE_MARK.BUNDLE_LOAD_START);
		}
	}, []);
	return <></>;
};

export const OpenMenuItem = (props: Props): React.JSX.Element => (
	<JSErrorBoundary
		id="asyncOpenMenuItemComponent"
		packageName="jiraSoftwareOnboardingQuickstart"
		teamName="jlove-makkuro"
		fallback="unmount"
		attributes={addChunkLoadErrorAnalyticsAttribute}
	>
		<Placeholder
			name="software-onboarding-checklist-open-menu-item"
			fallback={<SubmitOpenMenuItemLoadStart />}
		>
			<LazyOpenMenuItem {...props} />
		</Placeholder>
	</JSErrorBoundary>
);
